import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Play, Pause, SkipForward, SkipBack, Volume2, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MoodReactiveAvatar from '../components/MoodReactiveAvatar';

const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

const TTS_ENDPOINT = '/api/tts/edge';
const MAX_CHUNK_CHARS = 400;

const LANGUAGES = [
  { key: 'hindi', label: 'हिंदी' },
  { key: 'hinglish', label: 'Hinglish' },
  { key: 'english', label: 'English' }
];

const DOMAINS = [
  { key: 'all', label: 'All', icon: '✦' },
  { key: 'health', label: 'Health', icon: '♡' },
  { key: 'career', label: 'Career', icon: '⬆' },
  { key: 'wealth', label: 'Wealth', icon: '◈' },
  { key: 'marriage', label: 'Marriage', icon: '∞' },
  { key: 'family', label: 'Family', icon: '⌂' },
  { key: 'education', label: 'Education', icon: '✎' },
  { key: 'travel', label: 'Travel', icon: '✈' },
  { key: 'spiritual', label: 'Spiritual', icon: '☸' }
];

const DOMAIN_COLORS = {
  health: '#4ade80',
  career: '#60a5fa',
  wealth: '#facc15',
  marriage: '#f472b6',
  family: '#c084fc',
  education: '#2dd4bf',
  travel: '#fb923c',
  spiritual: '#a78bfa'
};

const splitTextIntoChunks = (text, maxChars = MAX_CHUNK_CHARS) => {
  if (!text || text.length <= maxChars) return text ? [text] : [];
  const chunks = [];
  let remaining = text;
  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining.trim());
      break;
    }
    let breakPoint = maxChars;
    for (const endChar of ['. ', '। ', '? ', '! ', '\n\n', '\n', ', ']) {
      const lastIndex = remaining.substring(0, maxChars).lastIndexOf(endChar);
      if (lastIndex > maxChars * 0.5) {
        breakPoint = lastIndex + endChar.length;
        break;
      }
    }
    chunks.push(remaining.substring(0, breakPoint).trim());
    remaining = remaining.substring(breakPoint).trim();
  }
  return chunks;
};

// Build narration text for a specific domain across all mahadashas
const buildDomainText = (mahadashas, currentMd, domain, language, name) => {
  let text = '';
  const domainLabel = domain.charAt(0).toUpperCase() + domain.slice(1);

  if (language === 'hindi') {
    text += `${name}, aapke ${domainLabel} ke baare mein poori kahani:\n\n`;
  } else if (language === 'english') {
    text += `${name}, here is your complete ${domainLabel} story:\n\n`;
  } else {
    text += `${name}, yeh hai aapki ${domainLabel} ki poori kahani:\n\n`;
  }

  mahadashas.forEach(md => {
    const effect = md.domains?.[domain];
    if (!effect) return;
    const theme = effect.theme || effect.level || '';
    if (language === 'hindi') {
      text += `${md.lord} Mahadasha (${md.years}): ${theme}\n\n`;
    } else if (language === 'english') {
      text += `During ${md.lord} period (${md.years}): ${theme}\n\n`;
    } else {
      text += `${md.lord} ki Mahadasha (${md.years}) mein: ${theme}\n\n`;
    }
  });

  if (currentMd) {
    const effect = currentMd.domains?.[domain];
    if (effect) {
      const theme = effect.theme || '';
      if (language === 'hindi') {
        text += `Abhi ${currentMd.lord} Mahadasha chal rahi hai: ${theme}\n`;
      } else if (language === 'english') {
        text += `Currently in ${currentMd.lord} period: ${theme}\n`;
      } else {
        text += `Abhi ${currentMd.lord} ki Mahadasha chal rahi hai: ${theme}\n`;
      }
    }
  }
  return text.trim();
};

// Build narration text for a specific mahadasha (all domains)
const buildMahadashaText = (md, language, name) => {
  let text = '';
  const isCurrent = md.is_current;

  if (language === 'hindi') {
    text += isCurrent
      ? `${name}, aapki abhi ${md.lord} Mahadasha chal rahi hai (${md.years}):\n\n`
      : `${name}, aapki ${md.lord} Mahadasha (${md.years}) ki kahani:\n\n`;
  } else if (language === 'english') {
    text += isCurrent
      ? `${name}, you are currently in ${md.lord} Mahadasha (${md.years}):\n\n`
      : `${name}, your ${md.lord} Mahadasha (${md.years}) story:\n\n`;
  } else {
    text += isCurrent
      ? `${name}, abhi aapki ${md.lord} ki Mahadasha chal rahi hai (${md.years}):\n\n`
      : `${name}, yeh rahi aapki ${md.lord} Mahadasha (${md.years}) ki kahani:\n\n`;
  }

  const domainKeys = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
  domainKeys.forEach(domain => {
    const effect = md.domains?.[domain];
    if (!effect) return;
    const theme = effect.theme || '';
    const label = domain.charAt(0).toUpperCase() + domain.slice(1);
    text += `${label}: ${theme}\n\n`;
  });

  return text.trim();
};

const PastStoryNew = () => {
  const navigate = useNavigate();

  // Core
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading your story...');
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [personName, setPersonName] = useState('');

  // Filters
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedMahadasha, setSelectedMahadasha] = useState(null); // null = none selected

  // Story data
  const [fullStoryText, setFullStoryText] = useState('');
  const [activeNarrationText, setActiveNarrationText] = useState('');
  const [displayedText, setDisplayedText] = useState('');
  const [mahadashas, setMahadashas] = useState([]);
  const [currentMahadashaData, setCurrentMahadashaData] = useState(null);

  // Audio
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [audioChunks, setAudioChunks] = useState([]);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [showHints, setShowHints] = useState(true);

  // Refs
  const audioRef = useRef(null);
  const audioCacheRef = useRef({});
  const abortRef = useRef(false);
  const storyDataRef = useRef(null);
  const nextAudioRef = useRef(null);
  const domainChipsRef = useRef(null);
  const mahadashaRef = useRef(null);
  const isPausedRef = useRef(false);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Determine what text to narrate based on current filters
  const getNarrationText = useCallback(() => {
    if (selectedMahadasha !== null) {
      const allMds = [...mahadashas];
      if (currentMahadashaData) allMds.push(currentMahadashaData);
      const md = allMds[selectedMahadasha];
      if (md) {
        if (selectedDomain !== 'all') {
          // Specific domain + specific mahadasha
          const effect = md.domains?.[selectedDomain];
          const theme = effect?.theme || 'No data available';
          return `${md.lord} Mahadasha (${md.years}) - ${selectedDomain}: ${theme}`;
        }
        return buildMahadashaText(md, selectedLanguage, personName);
      }
    }
    if (selectedDomain !== 'all') {
      return buildDomainText(mahadashas, currentMahadashaData, selectedDomain, selectedLanguage, personName);
    }
    return fullStoryText;
  }, [selectedDomain, selectedMahadasha, mahadashas, currentMahadashaData, selectedLanguage, personName, fullStoryText]);

  // TTS generation
  const generateTTSAudio = async (text, language) => {
    try {
      const response = await fetch(`${API_URL}${TTS_ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.substring(0, 500), language })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.audio) {
          // Android Capacitor WebView unreliably plays blob: URLs with
          // new Audio(). Use data URL directly — universally supported.
          return `data:audio/mp3;base64,${result.audio}`;
        }
      }
    } catch (e) {
      console.error('TTS error:', e.message);
    }
    return null;
  };

  // Preload audio chunks — starts playing first chunk immediately
  const preloadAllAudio = async (text, language) => {
    setIsPreloading(true);
    setPreloadProgress(0);
    abortRef.current = false;

    const chunks = splitTextIntoChunks(text, MAX_CHUNK_CHARS);
    const totalChunks = chunks.length;
    const loadedChunks = [];
    let totalDur = 0;
    const cachePrefix = selectedDomain !== 'all' ? `${selectedDomain}_` : selectedMahadasha !== null ? `md${selectedMahadasha}_` : '';

    for (let i = 0; i < totalChunks; i++) {
      if (abortRef.current) { setIsPreloading(false); return null; }

      const chunkText = chunks[i];
      const cacheKey = `past_${cachePrefix}${language}_${i}`;
      let audioUrl = audioCacheRef.current[cacheKey];

      if (!audioUrl) {
        if (i === 0) {
          setLoadingMessage('Starting narration...');
        } else {
          setLoadingMessage(`Loading narration... (${i + 1}/${totalChunks})`);
        }
        audioUrl = await generateTTSAudio(chunkText, language);
        if (audioUrl) audioCacheRef.current[cacheKey] = audioUrl;
      }

      let duration = 0;
      if (audioUrl) {
        try {
          const tempAudio = new Audio();
          tempAudio.setAttribute('playsinline', 'true');
          tempAudio.preload = 'metadata';
          tempAudio.src = audioUrl;
          await new Promise((resolve) => {
            tempAudio.addEventListener('loadedmetadata', () => { duration = tempAudio.duration || 0; resolve(); });
            tempAudio.addEventListener('error', resolve);
            setTimeout(resolve, 50);
          });
        } catch (e) {
          duration = (chunkText.split(/\s+/).length / 150) * 60;
        }
      }
      totalDur += duration;
      loadedChunks.push({ text: chunkText, audioUrl, duration, index: i });
      setPreloadProgress(Math.round(((i + 1) / totalChunks) * 100));

      // START PLAYING after first chunk is ready (don't wait for all)
      if (i === 0 && audioUrl) {
        setAudioChunks([...loadedChunks]);
        setTotalDuration(totalDur);
        setIsPreloading(false);
        setLoadingMessage('');
        // Trigger playback of first chunk immediately
        // Remaining chunks will be added as they load
        playAllChunks([...loadedChunks]);
      }
    }

    // Update with all loaded chunks (for skip/seek)
    setAudioChunks(loadedChunks);
    setTotalDuration(totalDur);
    if (loadedChunks.length > 0 && !loadedChunks[0].audioUrl) {
      // Edge case: first chunk failed, all loaded now
      setIsPreloading(false);
      setLoadingMessage('');
    }
    return loadedChunks;
  };

  const preloadNextChunk = (chunks, currentIndex) => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < chunks.length && chunks[nextIndex]?.audioUrl) {
      const nextAudio = new Audio();
      nextAudio.setAttribute('playsinline', 'true');
      nextAudio.preload = 'auto';
      nextAudio.src = chunks[nextIndex].audioUrl;
      nextAudioRef.current = nextAudio;
      nextAudio.load();
    }
  };

  const playChunk = async (chunk, index, allChunks) => {
    return new Promise((resolve) => {
      if (abortRef.current || !chunk.audioUrl) { resolve(); return; }
      setCurrentChunkIndex(index);
      setDisplayedText(chunk.text);

      let audio;
      if (nextAudioRef.current && nextAudioRef.current.src === chunk.audioUrl) {
        audio = nextAudioRef.current;
        nextAudioRef.current = null;
      } else {
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
        audio = new Audio();
        audio.setAttribute('playsinline', 'true');
        audio.preload = 'auto';
        audio.src = chunk.audioUrl;
      }
      audioRef.current = audio;
      if (allChunks) preloadNextChunk(allChunks, index);

      audio.onended = () => resolve();
      audio.onerror = (e) => {
        console.error('[PastStory] audio.onerror', {
          chunk: index,
          url: chunk.audioUrl,
          code: audio.error?.code,
          message: audio.error?.message,
          event: e,
        });
        resolve();
      };
      audio.ontimeupdate = () => {
        let elapsed = 0;
        for (let i = 0; i < index; i++) elapsed += audioChunks[i]?.duration || 0;
        elapsed += audio.currentTime;
        setCurrentTime(elapsed);
      };
      audio.play().catch((err) => {
        console.error('[PastStory] audio.play() rejected', {
          chunk: index,
          url: chunk.audioUrl,
          errorName: err?.name,
          errorMessage: err?.message,
        });
        resolve();
      });
    });
  };

  const playAllChunks = async (chunks) => {
    setIsPlaying(true);
    isPausedRef.current = false;
    abortRef.current = false;

    if (currentChunkIndex < chunks.length - 1) preloadNextChunk(chunks, currentChunkIndex);

    for (let i = currentChunkIndex; i < chunks.length; i++) {
      if (abortRef.current) break;
      while (isPausedRef.current && !abortRef.current) {
        await new Promise(r => setTimeout(r, 100));
      }
      if (abortRef.current) break;
      await playChunk(chunks[i], i, chunks);
    }
    if (!abortRef.current) setIsPlaying(false);
  };

  const unlockAudioContext = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const ctx = new AudioContext();
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
      }
    } catch (e) {}
  };

  // Stop current playback and reset audio state
  const stopPlayback = useCallback(() => {
    abortRef.current = true;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
    setIsPlaying(false);
    setIsPaused(false);
    isPausedRef.current = false;
    setCurrentChunkIndex(0);
    setAudioChunks([]);
    setCurrentTime(0);
    setTotalDuration(0);
    setDisplayedText('');
  }, []);

  const togglePlay = () => {
    if (isPreloading) return;
    unlockAudioContext();
    setShowHints(false);

    const narrationText = getNarrationText();
    if (!narrationText) return;

    if (!isPlaying && audioChunks.length === 0) {
      setActiveNarrationText(narrationText);
      // preloadAllAudio now auto-starts playback after first chunk loads
      preloadAllAudio(narrationText, selectedLanguage);
    } else if (isPlaying) {
      isPausedRef.current = true;
      setIsPaused(true);
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
    } else if (audioChunks.length > 0) {
      isPausedRef.current = false;
      setIsPaused(false);
      if (audioRef.current && audioRef.current.paused) audioRef.current.play();
      setIsPlaying(true);
      playAllChunks(audioChunks);
    }
  };

  const skipForward = () => {
    if (audioChunks.length > 0 && currentChunkIndex < audioChunks.length - 1) {
      abortRef.current = true;
      setTimeout(() => {
        setCurrentChunkIndex(prev => Math.min(prev + 1, audioChunks.length - 1));
        abortRef.current = false;
        if (isPlaying) playAllChunks(audioChunks);
      }, 100);
    }
  };

  const skipBackward = () => {
    if (audioChunks.length > 0 && currentChunkIndex > 0) {
      abortRef.current = true;
      setTimeout(() => {
        setCurrentChunkIndex(prev => Math.max(prev - 1, 0));
        abortRef.current = false;
        if (isPlaying) playAllChunks(audioChunks);
      }, 100);
    }
  };

  // Handle domain change
  const handleDomainChange = (domainKey) => {
    if (domainKey === selectedDomain) return;
    stopPlayback();
    setSelectedDomain(domainKey);
    setSelectedMahadasha(null);
    
    // Pre-cache first chunk for new domain in background
    setTimeout(async () => {
      const cachePrefix = domainKey !== 'all' ? `${domainKey}_` : '';
      const cacheKey = `past_${cachePrefix}${selectedLanguage}_0`;
      if (audioCacheRef.current[cacheKey]) return; // already cached
      
      // Build narration text for new domain
      let text;
      if (domainKey !== 'all') {
        text = buildDomainText(mahadashas, currentMahadashaData, domainKey, selectedLanguage, personName);
      } else {
        text = fullStoryText;
      }
      if (!text) return;
      
      const chunks = splitTextIntoChunks(text, MAX_CHUNK_CHARS);
      if (chunks.length > 0) {
        const audioUrl = await generateTTSAudio(chunks[0], selectedLanguage);
        if (audioUrl) audioCacheRef.current[cacheKey] = audioUrl;
      }
    }, 100);
  };

  // Handle mahadasha selection
  const handleMahadashaClick = (index) => {
    if (selectedMahadasha === index) {
      // Deselect
      stopPlayback();
      setSelectedMahadasha(null);
      return;
    }
    stopPlayback();
    setSelectedMahadasha(index);
  };

  // Handle language change
  const handleLanguageChange = (newLang) => {
    if (newLang === selectedLanguage) return;
    stopPlayback();
    setSelectedLanguage(newLang);

    if (storyDataRef.current?.ai_generated) {
      // AI story needs regeneration for new language - clear and reload
      storyDataRef.current = null;
    } else if (storyDataRef.current) {
      const newText = generateFullStoryText(storyDataRef.current, newLang);
      setFullStoryText(newText);
    }
  };

  // Generate full story text from unified data (fallback)
  const generateFullStoryText = useCallback((data, language) => {
    if (!data) return '';
    const pastMahadashas = data.past_story?.mahadashas || data.all_mahadashas?.filter(m => m.status === 'past') || [];
    const userName = data.user_info?.name || 'User';
    let fullText = '';
    const introText = data.past_story?.intro?.[language] || '';
    if (introText) fullText += introText + '\n\n';
    const timelineNarrations = data.past_story?.timeline_narrations?.[language] || [];
    pastMahadashas.forEach((md, idx) => {
      if (timelineNarrations[idx]) {
        const narr = timelineNarrations[idx];
        const narrationText = typeof narr === 'string' ? narr : (narr.text || '');
        if (narrationText) fullText += narrationText + '\n\n';
      } else {
        const lordName = md.lord || 'Unknown';
        const years = md.years || '';
        if (language === 'hindi') fullText += `${lordName} महादशा, ${years}।\n`;
        else if (language === 'english') fullText += `${lordName} Mahadasha, from ${years}.\n`;
        else fullText += `${lordName} ki Mahadasha, ${years}.\n`;
        if (md.domain_predictions) {
          ['health', 'career', 'wealth', 'marriage'].forEach(domain => {
            const pred = md.domain_predictions[domain];
            if (pred?.summary) fullText += `${domain}: ${pred.summary}\n`;
          });
        }
        fullText += '\n';
      }
    });
    return fullText.trim();
  }, []);

  // Load story data
  useEffect(() => {
    const loadData = async () => {
      if (storyDataRef.current) {
        if (storyDataRef.current.ai_generated) {
          setFullStoryText(storyDataRef.current.story);
        } else {
          setFullStoryText(generateFullStoryText(storyDataRef.current, selectedLanguage));
        }
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadingMessage('Preparing your journey...');

      const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (!birthDetails) { navigate('/onboarding-new'); return; }

      let birthData;
      try { birthData = JSON.parse(birthDetails); } catch (e) { navigate('/onboarding-new'); return; }

      const userName = birthData.name || 'User';
      setPersonName(userName);

      // Try AI story
      setLoadingMessage('Generating your story...');
      try {
        const aiResponse = await fetch(`${API_URL}/api/ai-story/past-story`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            birth_data: {
              name: birthData.name || 'User',
              year: birthData.year || parseInt(birthData.birthYear) || 1990,
              month: birthData.month || parseInt(birthData.birthMonth) || 1,
              day: birthData.day || parseInt(birthData.birthDay) || 1,
              hour: birthData.hour || parseInt(birthData.birthHour) || 12,
              minute: birthData.minute || parseInt(birthData.birthMinute) || 0,
              latitude: birthData.latitude || parseFloat(birthData.lat) || 28.6139,
              longitude: birthData.longitude || parseFloat(birthData.lng) || 77.209,
              gender: birthData.gender || 'male'
            },
            language: selectedLanguage
          })
        });

        if (aiResponse.ok) {
          const aiResult = await aiResponse.json();
          if (aiResult.success && aiResult.story) {
            storyDataRef.current = {
              ai_generated: true,
              story: aiResult.story,
              user_info: { name: userName }
            };
            setFullStoryText(aiResult.story);

            // Store structured mahadasha data for filtering
            if (aiResult.mahadashas) setMahadashas(aiResult.mahadashas);
            if (aiResult.current_mahadasha_data) setCurrentMahadashaData(aiResult.current_mahadasha_data);

            setIsLoading(false);
            setLoadingMessage('');
            return;
          }
        }
      } catch (aiError) {
        console.log('AI Story error:', aiError.message);
      }

      // Fallback: localStorage / MongoDB
      setLoadingMessage('Loading cached story...');
      const userId = localStorage.getItem('audio_user_id') || localStorage.getItem('session_id');
      const deviceId = localStorage.getItem('taraanaya_device_id');
      let unifiedStoryData = null;
      const storedData = localStorage.getItem('unified_story_data');
      if (storedData) {
        try {
          unifiedStoryData = JSON.parse(storedData);
          const currentBirthName = birthData.name?.toLowerCase();
          const storedName = unifiedStoryData.user_info?.name?.toLowerCase();
          if (currentBirthName && storedName && currentBirthName !== storedName) {
            localStorage.removeItem('unified_story_data');
            unifiedStoryData = null;
          }
        } catch (e) { localStorage.removeItem('unified_story_data'); }
      }
      if (!unifiedStoryData && (userId || deviceId)) {
        try {
          const queryParams = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : '';
          const lookupId = userId || deviceId;
          const response = await fetch(`${API_URL}/api/unified-story/load/${lookupId}${queryParams}`);
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) unifiedStoryData = result.data;
          }
        } catch (e) {}
      }
      if (!unifiedStoryData) { navigate('/generating-story'); return; }

      storyDataRef.current = unifiedStoryData;
      setFullStoryText(generateFullStoryText(unifiedStoryData, selectedLanguage));
      setIsLoading(false);
      setLoadingMessage('');
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, selectedLanguage]);

  // Pre-cache first TTS chunk in background after story loads
  // So first play tap is instant instead of 2-7s delay
  useEffect(() => {
    if (isLoading || !fullStoryText) return;
    
    const preCacheFirstChunk = async () => {
      const text = fullStoryText;
      const chunks = splitTextIntoChunks(text, MAX_CHUNK_CHARS);
      if (chunks.length === 0) return;
      
      const cacheKey = `past__${selectedLanguage}_0`; // default view (no domain/mahadasha prefix)
      if (audioCacheRef.current[cacheKey]) return; // already cached
      
      console.log('[PastStory] Pre-caching first TTS chunk in background...');
      const audioUrl = await generateTTSAudio(chunks[0], selectedLanguage);
      if (audioUrl) {
        audioCacheRef.current[cacheKey] = audioUrl;
        console.log('[PastStory] First chunk pre-cached!');
      }
    };
    
    // Small delay so UI renders first, then pre-cache in background
    const timer = setTimeout(preCacheFirstChunk, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, fullStoryText, selectedLanguage]);

  // Cleanup
  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
    };
  }, []);

  // Hide hints after 6s
  useEffect(() => {
    const timer = setTimeout(() => setShowHints(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  const allMahadashas = [...mahadashas, ...(currentMahadashaData ? [currentMahadashaData] : [])];
  const hasStructuredData = mahadashas.length > 0;

  // Get current filter label for display
  const getFilterLabel = () => {
    if (selectedMahadasha !== null && allMahadashas[selectedMahadasha]) {
      const md = allMahadashas[selectedMahadasha];
      if (selectedDomain !== 'all') {
        return `${md.lord} - ${selectedDomain.charAt(0).toUpperCase() + selectedDomain.slice(1)}`;
      }
      return `${md.lord} Mahadasha`;
    }
    if (selectedDomain !== 'all') {
      return DOMAINS.find(d => d.key === selectedDomain)?.label || selectedDomain;
    }
    return 'Full Story';
  };

  if (isLoading) {
    return (
      <div data-testid="past-story-loading" style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px'
      }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          style={{ width: '48px', height: '48px', border: '3px solid rgba(146,255,247,0.2)', borderTopColor: '#92FFF7', borderRadius: '50%', marginBottom: '20px' }} />
        <p style={{ color: '#9CA3AF', fontSize: 'clamp(12px, 3.2vw, 14px)' }}>{loadingMessage}</p>
      </div>
    );
  }

  return (
    <div data-testid="past-story-page" style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
      paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
      paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      paddingLeft: 'max(16px, env(safe-area-inset-left, 16px))',
      paddingRight: 'max(16px, env(safe-area-inset-right, 16px))',
      position: 'relative', overflow: 'hidden'
    }}>
      {/* Stars */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', opacity: 0.35, zIndex: 0 }}>
        {[8,22,45,68,85,12,35,58,78,92,5,28,52,72,88].map((l, i) => (
          <div key={i} style={{ position: 'absolute', width: `${1 + (i % 2)}px`, height: `${1 + (i % 2)}px`, borderRadius: '50%', background: 'white',
            left: `${l}%`, top: `${[15,8,12,5,18,38,52,45,62,48,75,82,88,78,92][i]}%`, boxShadow: `0 0 ${2 + (i % 3)}px white` }} />
        ))}
      </div>

      <div style={{ maxWidth: '480px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <button data-testid="back-button" onClick={() => navigate(-1)}
            style={{ padding: '8px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronLeft style={{ width: '22px', height: '22px', color: '#E8E8E8' }} />
          </button>

          {/* Language selector */}
          <div style={{ display: 'flex', gap: '4px', background: 'rgba(255,255,255,0.08)', padding: '3px 6px', borderRadius: '18px' }}>
            {LANGUAGES.map(lang => (
              <button key={lang.key} data-testid={`lang-${lang.key}`} onClick={() => handleLanguageChange(lang.key)}
                style={{
                  padding: '5px 10px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                  background: selectedLanguage === lang.key ? 'rgba(146,255,247,0.25)' : 'transparent',
                  color: selectedLanguage === lang.key ? '#92FFF7' : '#9CA3AF',
                  fontSize: 'clamp(9px, 2.5vw, 11px)', fontWeight: 600,
                  transition: 'background 0.2s, color 0.2s'
                }}>
                {lang.label}
              </button>
            ))}
          </div>
        </div>

        {/* Domain Filter Chips */}
        {hasStructuredData && (
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            {/* Instruction hint */}
            {showHints && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{ textAlign: 'center', marginBottom: '6px', fontSize: 'clamp(9px, 2.5vw, 11px)', color: 'rgba(146,255,247,0.7)', fontStyle: 'italic' }}>
                ↓ Choose domain for your past story ↓
              </motion.div>
            )}
            <div ref={domainChipsRef} data-testid="domain-filter" style={{
              display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px',
              scrollbarWidth: 'none', msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}>
              {DOMAINS.map(d => {
                const isActive = selectedDomain === d.key;
                const color = d.key === 'all' ? '#92FFF7' : DOMAIN_COLORS[d.key] || '#92FFF7';
                return (
                  <button key={d.key} data-testid={`domain-chip-${d.key}`} onClick={() => handleDomainChange(d.key)}
                    style={{
                      flexShrink: 0, padding: '6px 12px', borderRadius: '16px', border: `1px solid ${isActive ? color : 'rgba(255,255,255,0.15)'}`,
                      background: isActive ? `${color}22` : 'rgba(255,255,255,0.05)',
                      color: isActive ? color : '#9CA3AF',
                      fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: isActive ? 700 : 500,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease'
                    }}>
                    <span style={{ marginRight: '4px', fontSize: '10px' }}>{d.icon}</span>{d.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Compact Avatar + Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <div style={{ flexShrink: 0 }}>
            <MoodReactiveAvatar mood="wise" isPlaying={isPlaying} size="small" showParticles={isPlaying} />
          </div>
          <div>
            <h1 style={{
              fontFamily: "Aptos, 'Segoe UI', sans-serif", fontSize: 'clamp(16px, 4vw, 22px)', fontWeight: 600,
              color: '#E8E8E8', letterSpacing: '0.3px', marginBottom: '2px', lineHeight: '1.2'
            }}>
              {personName ? `${personName}'s Past Story` : 'Your Past Story'}
            </h1>
            <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: '#9CA3AF', lineHeight: '1.4' }}>
              {isPreloading ? `Loading narration... ${preloadProgress}%`
                : selectedDomain !== 'all' || selectedMahadasha !== null
                  ? getFilterLabel()
                  : 'Discover what your stars reveal'}
            </p>
          </div>
        </div>

        {/* Audio Player */}
        <div data-testid="audio-player" style={{
          background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: '18px', padding: '18px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', marginBottom: '16px'
        }}>
          {/* Filter indicator */}
          {(selectedDomain !== 'all' || selectedMahadasha !== null) && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              marginBottom: '12px', padding: '4px 10px', borderRadius: '10px',
              background: 'rgba(146,255,247,0.1)', fontSize: 'clamp(10px, 2.5vw, 11px)', color: '#92FFF7'
            }}>
              <Filter style={{ width: '12px', height: '12px' }} />
              <span>Playing: {getFilterLabel()}</span>
            </div>
          )}

          {/* Progress Bar */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ width: '100%', height: '5px', background: 'rgba(255,255,255,0.15)', borderRadius: '3px', overflow: 'hidden' }}>
              <motion.div animate={{ width: isPreloading ? `${preloadProgress}%` : totalDuration > 0 ? `${(currentTime / totalDuration) * 100}%` : '0%' }}
                style={{
                  height: '100%', borderRadius: '3px',
                  background: isPreloading ? 'linear-gradient(90deg, #92FFF7, #a855f7)' : `linear-gradient(90deg, ${selectedDomain !== 'all' ? DOMAIN_COLORS[selectedDomain] || '#a855f7' : '#a855f7'}, #92FFF7)`
                }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: 'clamp(9px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)' }}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(totalDuration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
            <button data-testid="skip-back-btn" onClick={skipBackward} disabled={isPreloading || currentChunkIndex === 0}
              style={{ padding: '10px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: 'none', cursor: isPreloading ? 'not-allowed' : 'pointer', opacity: isPreloading || currentChunkIndex === 0 ? 0.4 : 1 }}>
              <SkipBack style={{ width: '18px', height: '18px', color: '#E8E8E8' }} />
            </button>
            <button data-testid="play-pause-btn" onClick={togglePlay} disabled={isPreloading}
              style={{
                padding: '14px', borderRadius: '50%', border: 'none', cursor: isPreloading ? 'not-allowed' : 'pointer',
                background: isPreloading ? 'rgba(146,255,247,0.3)' : `linear-gradient(135deg, ${selectedDomain !== 'all' ? DOMAIN_COLORS[selectedDomain] || '#a855f7' : '#a855f7'}, #92FFF7)`,
                boxShadow: '0 4px 20px rgba(168,85,247,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
              {isPreloading ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  style={{ width: '24px', height: '24px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%' }} />
              ) : isPlaying ? (
                <Pause style={{ width: '24px', height: '24px', color: '#fff' }} />
              ) : (
                <Play style={{ width: '24px', height: '24px', color: '#fff', marginLeft: '2px' }} />
              )}
            </button>
            <button data-testid="skip-fwd-btn" onClick={skipForward} disabled={isPreloading || currentChunkIndex >= audioChunks.length - 1}
              style={{ padding: '10px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: 'none', cursor: isPreloading ? 'not-allowed' : 'pointer', opacity: isPreloading || currentChunkIndex >= audioChunks.length - 1 ? 0.4 : 1 }}>
              <SkipForward style={{ width: '18px', height: '18px', color: '#E8E8E8' }} />
            </button>
          </div>

          {audioChunks.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: 'clamp(9px, 2.3vw, 10px)', color: 'rgba(255,255,255,0.4)' }}>
              Part {currentChunkIndex + 1} of {audioChunks.length}
            </div>
          )}
        </div>

        {/* Narration Text */}
        <AnimatePresence mode="wait">
          {displayedText && (
            <motion.div key={displayedText.substring(0, 30)} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              data-testid="narration-text" style={{
                background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)',
                border: `1px solid ${selectedDomain !== 'all' ? `${DOMAIN_COLORS[selectedDomain]}33` : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '14px', padding: '16px', maxHeight: '160px', overflow: 'auto', marginBottom: '16px'
              }}>
              <p style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', lineHeight: '1.8', color: '#E8E8E8', whiteSpace: 'pre-line', margin: 0 }}>
                {displayedText}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mahadasha Timeline */}
        {hasStructuredData && allMahadashas.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            {showHints && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                style={{ textAlign: 'center', marginBottom: '6px', fontSize: 'clamp(9px, 2.5vw, 11px)', color: 'rgba(146,255,247,0.7)', fontStyle: 'italic' }}>
                ↑ Tap a Mahadasha to hear its story ↑
              </motion.div>
            )}
            <div style={{
              fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.5)',
              marginBottom: '8px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase'
            }}>
              Mahadasha Timeline
            </div>
            <div ref={mahadashaRef} data-testid="mahadasha-timeline" style={{
              display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px',
              scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch'
            }}>
              {allMahadashas.map((md, idx) => {
                const isActive = selectedMahadasha === idx;
                const isCurrent = md.is_current;
                return (
                  <button key={idx} data-testid={`mahadasha-${idx}`} onClick={() => handleMahadashaClick(idx)}
                    style={{
                      flexShrink: 0, padding: '8px 14px', borderRadius: '12px', cursor: 'pointer',
                      border: `1px solid ${isActive ? '#92FFF7' : isCurrent ? 'rgba(250,204,21,0.4)' : 'rgba(255,255,255,0.12)'}`,
                      background: isActive ? 'rgba(146,255,247,0.15)' : isCurrent ? 'rgba(250,204,21,0.08)' : 'rgba(255,255,255,0.05)',
                      transition: 'all 0.2s ease', textAlign: 'left'
                    }}>
                    <div style={{
                      fontSize: 'clamp(11px, 3vw, 13px)', fontWeight: 600,
                      color: isActive ? '#92FFF7' : isCurrent ? '#facc15' : '#E8E8E8',
                      marginBottom: '2px', whiteSpace: 'nowrap'
                    }}>
                      {md.lord}{isCurrent ? ' ★' : ''}
                    </div>
                    <div style={{ fontSize: 'clamp(9px, 2.3vw, 10px)', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                      {md.years || `Age ${md.start_age}-${md.end_age}`}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', paddingTop: '4px' }}>
          <button data-testid="read-full-story" onClick={() => navigate('/your-story')}
            style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', color: '#92FFF7', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
            Read Full Story →
          </button>
          <button data-testid="skip-button" onClick={() => navigate('/login')}
            style={{ fontSize: 'clamp(11px, 2.8vw, 13px)', color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 20px' }}>
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PastStoryNew;
